import { useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import { Check, Film, Image as ImageIcon, Music2, Plus } from "lucide-react";
import {
  createLibraryCategory, saveJobResultToLibrary, swrFetcher,
  updateLibraryItem, type JobInfo, type LibraryCategoryInfo, type LibraryItemInfo,
} from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Input, Textarea } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface Props {
  job: JobInfo;
  open: boolean;
  initialItem?: LibraryItemInfo | null;
  onClose: () => void;
  onSaved: (item: LibraryItemInfo) => void;
}

export function SaveToLibraryDialog({ job, open, initialItem, onClose, onSaved }: Props) {
  const assets = job.result.assets ?? [];
  const promptValue = typeof job.params.prompt === "string" ? job.params.prompt : "";
  const [selectedKey, setSelectedKey] = useState(assets[0]?.storage_key ?? "");
  const [title, setTitle] = useState(promptValue.trim().slice(0, 72) || "Untitled generation");
  const [prompt, setPrompt] = useState(promptValue);
  const scope = "personal" as const;
  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  const [newCategory, setNewCategory] = useState("");
  const [saving, setSaving] = useState(false);
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [existingItem, setExistingItem] = useState<LibraryItemInfo | null>(null);
  const { data: categories, mutate: mutateCategories } = useSWR<LibraryCategoryInfo[]>(
    open ? "/api/library/categories" : null, swrFetcher
  );

  useEffect(() => {
    if (!open) return;
    setSelectedKey(assets[0]?.storage_key ?? "");
    setTitle(initialItem?.title ?? (promptValue.trim().slice(0, 72) || "Untitled generation"));
    setPrompt(initialItem?.prompt ?? promptValue);
    setCategoryIds(initialItem?.categories.map((category) => category.id) ?? []);
    setError(null);
    setExistingItem(initialItem ?? null);
  }, [open, job.id, initialItem?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const usableCategories = useMemo(
    () => (categories ?? []).filter((category) =>
      !category.archived && (scope === "personal" || category.scope !== "personal")
    ),
    [categories, scope]
  );

  useEffect(() => {
    if (!categories) return;
    const usable = new Set(usableCategories.map((category) => category.id));
    setCategoryIds((current) => current.filter((id) => usable.has(id)));
  }, [categories, usableCategories]);

  async function addCategory() {
    if (!newCategory.trim()) return;
    setCreatingCategory(true); setError(null);
    try {
      const category = await createLibraryCategory({ name: newCategory.trim(), scope });
      await mutateCategories((current) => [...(current ?? []), category], { revalidate: false });
      setCategoryIds((current) => [...current, category.id]);
      setNewCategory("");
    } catch (err) { setError(err instanceof Error ? err.message : "Category could not be created"); }
    finally { setCreatingCategory(false); }
  }

  async function save() {
    const asset = assets.find((candidate) => candidate.storage_key === selectedKey);
    if (!asset || !title.trim() || !prompt.trim()) return;
    setSaving(true); setError(null);
    try {
      const editingExisting = Boolean(existingItem);
      if (editingExisting) {
        const item = await updateLibraryItem(existingItem!.id, {
          title: title.trim(), prompt: prompt.trim(), category_ids: categoryIds,
        });
        setExistingItem(item);
        onSaved(item);
        return;
      }
      const item = await saveJobResultToLibrary({
        job_id: job.id, asset_id: asset.asset_id, storage_key: asset.storage_key,
        title: title.trim(), prompt: prompt.trim(), scope, category_ids: categoryIds,
      });
      onSaved(item);
      if (item.duplicate) {
        setExistingItem(item);
        setTitle(item.title);
        setPrompt(item.prompt);
        setCategoryIds(item.categories.map((category) => category.id));
      }
      else onClose();
    } catch (err) { setError(err instanceof Error ? err.message : "Could not save to Library"); }
    finally { setSaving(false); }
  }

  const AssetIcon = ({ type }: { type: string }) =>
    type === "video" ? <Film className="h-4 w-4" />
      : type === "audio" ? <Music2 className="h-4 w-4" />
        : <ImageIcon className="h-4 w-4" />;
  const editingExisting = Boolean(existingItem);

  return <Dialog open={open} onClose={onClose} title="Save to Library">
    <div data-testid="save-library-dialog" className="space-y-5 overflow-y-auto p-4 sm:p-5">
      {editingExisting && (
        <div className="rounded-xl border border-studio-accent/30 bg-studio-accent/10 px-3 py-2.5 text-xs text-studio-muted">
          This output already exists in the selected scope. You are editing the existing Library item.
        </div>
      )}
      {assets.length > 1 && <div><label className="mb-2 block text-[11px] font-semibold uppercase tracking-wider text-studio-muted">Output</label><div className="grid grid-cols-1 gap-2 sm:grid-cols-2">{assets.map((asset, index) => <button type="button" key={asset.storage_key} onClick={() => setSelectedKey(asset.storage_key)} className={cn("flex min-h-11 items-center gap-2 rounded-lg border px-3 py-2 text-left text-xs", selectedKey === asset.storage_key ? "border-studio-accent bg-studio-accent-soft text-studio-accent" : "border-studio-border text-studio-muted")}><AssetIcon type={asset.type} /> Output {index + 1}</button>)}</div></div>}
      <div><label className="mb-2 block text-[11px] font-semibold uppercase tracking-wider text-studio-muted">Title</label><Input value={title} onChange={(event) => setTitle(event.target.value)} maxLength={180} /></div>
      <div><label className="mb-2 block text-[11px] font-semibold uppercase tracking-wider text-studio-muted">Prompt</label><Textarea value={prompt} onChange={(event) => setPrompt(event.target.value)} rows={5} /></div>
      <div><label className="mb-2 block text-[11px] font-semibold uppercase tracking-wider text-studio-muted">Visibility</label><p className="text-sm text-studio-muted">Private to the owner. Team administrators can review this item.</p></div>
      <div>
        <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wider text-studio-muted">Categories</label>
        <div className="flex max-h-32 flex-wrap gap-2 overflow-y-auto rounded-xl border border-studio-border bg-studio-card p-3">
          {usableCategories.length === 0 && <span className="text-xs text-studio-faint">No categories yet</span>}
          {usableCategories.map((category) => <button key={category.id} type="button" onClick={() => setCategoryIds((current) => current.includes(category.id) ? current.filter((id) => id !== category.id) : [...current, category.id])} className={cn("min-h-10 rounded-full border px-2.5 py-1 text-[11px]", categoryIds.includes(category.id) ? "border-studio-accent bg-studio-accent text-studio-accent-fg" : "border-studio-border text-studio-muted")}><span className="inline-flex items-center gap-1">{categoryIds.includes(category.id) && <Check className="h-3 w-3" />}{category.name}</span></button>)}
        </div>
        <div className="mt-2 flex gap-2"><Input value={newCategory} onChange={(event) => setNewCategory(event.target.value)} placeholder={`New ${scope} category`} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); void addCategory(); } }} /><Button type="button" variant="secondary" size="icon" loading={creatingCategory} disabled={!newCategory.trim()} onClick={() => void addCategory()} aria-label="Create category"><Plus className="h-4 w-4" /></Button></div>
        <div className="mt-2 flex flex-wrap gap-1.5">{categoryIds.map((id) => { const category = usableCategories.find((item) => item.id === id); return category ? <Badge key={id}>{category.name}</Badge> : null; })}</div>
      </div>
      {error && <p className="rounded-lg bg-studio-danger/10 px-3 py-2 text-xs text-studio-danger">{error}</p>}
    </div>
    <div className="grid grid-cols-1 gap-2 border-t border-studio-border px-4 py-4 sm:flex sm:justify-end sm:px-5"><Button variant="secondary" onClick={onClose}>{editingExisting ? "Done" : "Cancel"}</Button><Button loading={saving} disabled={!selectedKey || !title.trim() || !prompt.trim()} onClick={() => void save()}>{editingExisting ? "Update item" : "Save to Library"}</Button></div>
  </Dialog>;
}
